import React from "react";
import { NavLink } from "react-router-dom";
import { CountBadge } from "../support/ChatPanel";
import { useSupportUnread } from "../../hooks/useSupportUnread";

interface Props {
  /** Per-page tagline shown under the "Platform Admin" title. */
  subtitle: string;
}

const TABS = [
  { to: "/platform/clinics", label: "Onboarded Clinics" },
  { to: "/platform/kyc", label: "KYC Review Queue" },
  { to: "/platform/revenue", label: "Revenue" },
  { to: "/platform/support-tickets", label: "Support Tickets" },
];

/**
 * Shared header for the Platform Admin area: shield + title + subtitle, and the
 * Onboarded Clinics / KYC Review Queue / Revenue / Support Tickets tab strip.
 * The active tab is derived from the route (KYC tab stays lit on the
 * /platform/kyc/:id detail page too).
 */
const PlatformAdminHeader: React.FC<Props> = ({ subtitle }) => {
  const { summary } = useSupportUnread("admin");
  return (
  <div className="mb-6">
    <div className="flex items-start gap-3">
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="w-6 h-6 text-[#2563eb] mt-0.5 flex-shrink-0"
      >
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
        <path d="M9 12l2 2 4-4" />
      </svg>
      <div>
        <h1 className="text-xl font-bold text-slate-900 leading-tight">Platform Admin</h1>
        <p className="text-sm text-slate-500 mt-0.5">{subtitle}</p>
      </div>
    </div>

    <nav className="mt-4 flex items-center gap-6 border-b border-slate-200">
      {TABS.map(t => (
        <NavLink
          key={t.to}
          to={t.to}
          end={false}
          className={({ isActive }) =>
            `relative px-1 pb-3 text-sm font-medium transition-colors ${
              isActive ? "text-slate-900" : "text-slate-400 hover:text-slate-600"
            }`
          }
        >
          {({ isActive }) => (
            <>
              {t.label}
              {t.to === "/platform/support-tickets" && <CountBadge count={summary.total} className="ml-1.5 align-middle" />}
              {isActive && (
                <span className="absolute left-0 -bottom-px h-0.5 w-full rounded-full bg-[#2563eb]" />
              )}
            </>
          )}
        </NavLink>
      ))}
    </nav>
  </div>
  );
};

export default PlatformAdminHeader;
